import api from "../api";
import config from "../config";

export const NEXT_LAUNCHPAD_TARGETS = Object.freeze({
  STANDARD_LAUNCHPAD: "standard_launchpad",
  PARTNER_REFINEMENTS: "partner_refinements",
});

const LAUNCHPAD_SETUP_REDIRECT_ATTEMPT_KEY = "launchpad_setup_redirect_attempted";

export const isNextLaunchpadEnabledForUser = ({ user } = {}) => {
  const enabledUserIds = config.nextLaunchpadEnabledUserIds || [];
  const userId = Number(user?.user_id);

  return enabledUserIds.includes(userId);
};

export const isNextLaunchpadRefinementsEnabledForUser = ({ user } = {}) => {
  return (
    user?.can_manage_ai_receptionist === true ||
    user?.can_manage_ai_receptionist === 1 ||
    user?.can_manage_ai_receptionist === "1" ||
    user?.can_manage_ai_receptionist === "yes"
  );
};

export const shouldRedirectToLaunchpadSetup = (user) =>
  (user?.requires_launchpad_setup === true ||
    user?.requires_launchpad_setup === 1) &&
  ["joist", "invoice-simple"].includes(
    String(user?.plg_account_type || "")
      .trim()
      .toLowerCase()
  );

export const hasLaunchpadSetupRedirectBeenAttempted = () =>
  sessionStorage.getItem(LAUNCHPAD_SETUP_REDIRECT_ATTEMPT_KEY) === "1";

export const clearLaunchpadSetupRedirectAttempt = () => {
  sessionStorage.removeItem(LAUNCHPAD_SETUP_REDIRECT_ATTEMPT_KEY);
};

export const isReturningFromLaunchpadOrigin = () => {
  const allowedOrigins = config.nextLaunchpadAllowedOrigins || [];

  if (!document.referrer) {
    return false;
  }

  try {
    return allowedOrigins.includes(new URL(document.referrer).origin);
  } catch {
    return false;
  }
};

export const shouldAttemptLaunchpadSetupRedirect = (user) => {
  if (!shouldRedirectToLaunchpadSetup(user)) {
    clearLaunchpadSetupRedirectAttempt();
    return false;
  }

  if (isReturningFromLaunchpadOrigin() || hasLaunchpadSetupRedirectBeenAttempted()) {
    return false;
  }

  return true;
};

const canOpenNextLaunchpadTarget = ({ user, target }) => {
  if (target === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS) {
    return (
      isNextLaunchpadRefinementsEnabledForUser({ user }) ||
      (user?.plg_account_type === "invoice-simple" &&
        shouldRedirectToLaunchpadSetup(user))
    );
  }

  return (
    isNextLaunchpadEnabledForUser({ user }) ||
    shouldRedirectToLaunchpadSetup(user)
  );
};

const assertAllowedRedirectUrl = ({ redirectUrl }) => {
  const allowedOrigins = config.nextLaunchpadAllowedOrigins || [];
  let parsedUrl;

  try {
    parsedUrl = new URL(redirectUrl);
  } catch {
    throw new Error("Next launchpad handoff returned an invalid redirect URL.");
  }

  if (!["http:", "https:"].includes(parsedUrl.protocol)) {
    throw new Error("Next launchpad handoff returned an invalid redirect URL.");
  }

  if (!allowedOrigins.includes(parsedUrl.origin)) {
    throw new Error("Next launchpad handoff returned an untrusted redirect URL.");
  }
};

const markLaunchpadSetupRedirectAttempted = () => {
  sessionStorage.setItem(LAUNCHPAD_SETUP_REDIRECT_ATTEMPT_KEY, "1");
};

export const openNextLaunchpad = async ({
  user,
  target = NEXT_LAUNCHPAD_TARGETS.STANDARD_LAUNCHPAD,
} = {}) => {
  if (!canOpenNextLaunchpadTarget({ user, target })) {
    return;
  }

  const response = await api.user.createNextLaunchpadHandoff({ target });
  const redirectUrl =
    typeof response?.data?.redirect_url === "string"
      ? response.data.redirect_url.trim()
      : "";

  if (!redirectUrl) {
    throw new Error("Next launchpad handoff did not return a redirect URL.");
  }

  assertAllowedRedirectUrl({ redirectUrl });
  window.location.assign(redirectUrl);
};

export const redirectToLaunchpadSetup = async ({ user } = {}) => {
  if (!shouldAttemptLaunchpadSetupRedirect(user)) {
    return false;
  }

  markLaunchpadSetupRedirectAttempted();
  await openNextLaunchpad({
    user,
    target:
      user?.plg_account_type === "invoice-simple"
        ? NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS
        : NEXT_LAUNCHPAD_TARGETS.STANDARD_LAUNCHPAD,
  });
  return true;
};
